import React, { useMemo } from "react";
import axiosInstance from "../../services/api/axios";
import { toast } from "react-toastify";
import { FormModal, Form, Field, FormSubmit, AddressFields } from "../../components/form";
import { editUserSchema } from "../../services/users/schemas";

/**
 * Edit a property-buyer user. Self-contained: hits PUT /users?userId={id}
 * directly. Use EditUserModal when the parent wants to own the endpoint call.
 *
 * (These two screens edit the same entity and were nearly duplicates before
 * the refactor; both now share editUserSchema and AddressFields, so any
 * future drift will be at the call-site level — not the schema or layout.)
 */
const GENDER_OPTIONS = [
  { value: "MALE", label: "Male" },
  { value: "FEMALE", label: "Female" },
];

const UserEditModal = ({ user, onClose }) => {
  const defaultValues = useMemo(
    () => ({
      firstName: user?.firstName || "",
      lastName: user?.lastName || "",
      mobileNoWa: user?.mobileNoWa || "",
      mobileNoCalling: user?.mobileNoCalling || "",
      emailId: user?.emailId || "",
      gender: user?.gender || "",
      dob: user?.dob || "",
      address: {
        buildingNo: user?.address?.buildingNo || "",
        streetName1: user?.address?.streetName1 || "",
        streetName2: user?.address?.streetName2 || "",
        city: user?.address?.city || "",
        district: user?.address?.district || "",
        state: user?.address?.state || "",
        pinCode: user?.address?.pinCode || "",
        country: user?.address?.country || "India",
      },
    }),
    [user],
  );

  const handleSave = async (data) => {
    try {
      await axiosInstance.put(`/users?userId=${user.userId}`, data);
      toast.success("User updated.");
      onClose?.();
    } catch (error) {
      console.error("Error updating user:", error);
      toast.error(error?.detail || error?.response?.data?.detail || "Failed to update user.");
    }
  };

  return (
    <FormModal open onClose={onClose} title="Edit User Details" size="xl">
      <Form
        key={user?.userId ?? "new"}
        schema={editUserSchema}
        defaultValues={defaultValues}
        onSubmit={handleSave}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field name="firstName" label="First Name" required />
          <Field name="lastName" label="Last Name" required />
          <Field name="emailId" label="Email" type="email" required />
          <Field name="mobileNoWa" label="Phone (WhatsApp)" type="phone" required />
          <Field name="mobileNoCalling" label="Phone (Calling)" type="phone" required />
          <Field name="gender" label="Gender" type="select" options={GENDER_OPTIONS} />
          <Field name="dob" label="Date of Birth" type="date" />
        </div>

        <h3 className="text-sm font-semibold text-slate-700 mt-6 mb-2">Address</h3>
        <AddressFields prefix="address" required />

        <FormSubmit>Save Changes</FormSubmit>
      </Form>
    </FormModal>
  );
};

export default UserEditModal;
